import { MERGE_POLICY_OPTIONS } from "@/components/teams/merge-policy-card";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import { useSetFastMode, useSetMergePolicy, useTeamTree } from "@/hooks/queries/use-teams";
import { ApiError } from "@/lib/api/client";
import type { MergePolicy } from "@/lib/api/types";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";
import { projectRows, type ProjectRow } from "../projects/all-projects";

/**
 * Automation defaults (docs/new-look/PLAN.md, step 8.2): what runs do on their
 * own, per project, with only what exists today: who merges the pull requests
 * and whether builds start in fast mode. "Run every step for me" and a model
 * per role come later (L5).
 */

function errorText(error: unknown): string | null {
  if (!error) return null;
  return error instanceof ApiError ? error.message : "Couldn't save. Try again.";
}

function ProjectDefaults({ row, canEdit }: { row: ProjectRow; canEdit: boolean }) {
  const { project, team } = row;
  const merge = useSetMergePolicy(project.id);
  const fast = useSetFastMode(project.id);
  // Show the saved value straight away; the tree reloads on its own later.
  const policy: MergePolicy =
    (merge.isPending ? merge.variables : undefined) ??
    merge.data?.data.mergePolicy ??
    project.mergePolicy ??
    "never";
  const fastMode =
    (fast.isPending ? fast.variables : undefined) ??
    fast.data?.data.fastMode ??
    project.fastMode ??
    false;
  const error = errorText(merge.error) ?? errorText(fast.error);
  const label = project.name;

  return (
    <li className="flex flex-col gap-3 border-b border-border py-3 last:border-b-0 md:flex-row md:items-center">
      <div className="min-w-0 md:flex-1">
        <div className="truncate text-sm font-medium text-foreground">{label}</div>
        {team && <div className="truncate text-[13px] text-muted-foreground">{team.name}</div>}
        {error && (
          <div role="alert" className="text-[13px] text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm md:w-56">
        <span className="shrink-0 text-muted-foreground md:sr-only">Merging</span>
        <select
          aria-label={`Who merges for ${label}`}
          value={policy}
          disabled={!canEdit || merge.isPending}
          onChange={(e) => {
            merge.mutate(e.target.value as MergePolicy);
          }}
          className="min-h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground disabled:opacity-60"
        >
          {MERGE_POLICY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <div className="flex items-center gap-2 text-sm md:w-36 md:justify-end">
        <span className="text-muted-foreground md:sr-only">Fast mode</span>
        <button
          type="button"
          role="switch"
          aria-checked={fastMode}
          aria-label={`Fast mode for ${label}`}
          disabled={!canEdit || fast.isPending}
          onClick={() => {
            fast.mutate(!fastMode);
          }}
          className={cn(
            "relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60",
            fastMode ? "bg-primary" : "bg-muted-foreground/30"
          )}
        >
          <span
            className={cn(
              "inline-block h-6 w-6 rounded-full bg-white shadow transition-transform",
              fastMode ? "translate-x-5" : "translate-x-0.5"
            )}
          />
        </button>
      </div>
    </li>
  );
}

export function AutomationDefaults() {
  const me = useAuthStore((s) => s.user);
  const { data: onboarding } = useOnboardingState();
  const { data: tree = [], isLoading } = useTeamTree();
  const rows = projectRows(tree);
  // Organization and platform admins change these here; a project's own admins
  // can still change their project on its page.
  const canEdit =
    !!me?.isGlobalAdmin ||
    (onboarding?.step === "ready" && onboarding.organizationRole === "admin");

  return (
    <div className="flex flex-col gap-3">
      <dl className="grid gap-2 text-[13px] text-muted-foreground sm:grid-cols-2">
        {MERGE_POLICY_OPTIONS.map((o) => (
          <div key={o.value}>
            <dt className="inline font-medium text-foreground">{o.label}: </dt>
            <dd className="inline">{o.help}</dd>
          </div>
        ))}
        <div>
          <dt className="inline font-medium text-foreground">Fast mode: </dt>
          <dd className="inline">Builds start with faster output, at a higher cost.</dd>
        </div>
      </dl>
      {!canEdit && (
        <p className="text-[13px] text-muted-foreground">
          Only an admin can change these here. A project's admins can change it on the project's
          page.
        </p>
      )}
      {isLoading ? (
        <div className="h-24 animate-pulse rounded-lg bg-muted" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No projects yet.</p>
      ) : (
        <>
          <div
            aria-hidden="true"
            className="hidden border-b border-border pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground md:flex"
          >
            <span className="flex-1">Project</span>
            <span className="w-56">Who merges</span>
            <span className="w-36 text-right">Fast mode</span>
          </div>
          <ul>
            {rows.map((row) => (
              <ProjectDefaults key={row.project.id} row={row} canEdit={canEdit} />
            ))}
          </ul>
        </>
      )}
      <p className="text-[13px] text-muted-foreground">
        Coming later: "Run every step for me" and a model for each helper.
      </p>
    </div>
  );
}
